// Push Notification Utilities

const PUBLIC_VAPID_KEY = 'BH2BHcvR3L6dh8BOy86MMXk2JRVZi31cQtPMzfuR2rC_56F3vgJwg44F6d5iIAA3v_-GmxDDzzoPDK2xknSnfP4';

// Convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Detect iOS
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Detect if running as PWA (installed to home screen)
export const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
};

// Check if notifications are supported
export const isNotificationSupported = () => {
  // On iOS, notifications only work if installed as PWA
  if (isIOS() && !isPWA()) {
    return false;
  }
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    throw new Error('Notifications are not supported in this browser');
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

// Get notification permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Get user-friendly error message
export const getNotificationError = () => {
  if (isIOS() && !isPWA()) {
    return {
      type: 'ios-pwa-required',
      message: 'On iPhone, you must install this app to your home screen first to enable notifications.',
      instructions: [
        'Tap the Share button (square with arrow) at the bottom of Safari',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner',
        'Open the app from your home screen',
        'Return here to enable notifications'
      ]
    };
  }
  
  if (!('serviceWorker' in navigator)) {
    return {
      type: 'no-service-worker',
      message: 'Your browser doesn\'t support notifications.',
      instructions: ['Please use a modern browser like Chrome, Safari, Firefox, or Edge']
    };
  }
  
  if (!('PushManager' in window)) {
    return {
      type: 'no-push',
      message: 'Push notifications are not supported in your browser.',
      instructions: ['Try using Safari on iPhone or Chrome on Android']
    };
  }
  
  return null;
};

// Register service worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }
};

// Subscribe to push notifications
export const subscribeToPushNotifications = async (API, token) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
    });

    // Send subscription to backend
    const response = await fetch(`${API}/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(subscription)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.detail || 'Failed to save subscription');
    }

    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error);
    throw error;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPushNotifications = async (API, token) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      
      // Notify backend
      await fetch(`${API}/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
    }
  } catch (error) {
    console.error('Failed to unsubscribe from push notifications:', error);
    throw error;
  }
};

// Check if user is subscribed
export const isSubscribedToPushNotifications = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Failed to check subscription status:', error);
    return false;
  }
};

// Show a test notification
export const showTestNotification = async () => {
  if (!isNotificationSupported()) {
    throw new Error('Notifications are not supported');
  }

  if (Notification.permission === 'granted') {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification('WFMC Discipleship Journey', {
      body: 'Test notification - You will receive daily reminders like this!',
      icon: '/wfmc-logo.png',
      badge: '/wfmc-logo.png',
      vibrate: [200, 100, 200]
    });
  }
};
